import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Calendar, Clock } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import Seo from "@/components/Seo";
import { getPost, sortedPosts } from "@/content/blogPosts";
import { SITE_URL, SITE_NAME, EMAILS } from "@/lib/seo";

const fmt = (d) =>
  new Date(d).toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" });

function Block({ block }) {
  switch (block.t) {
    case "h2":
      return (
        <h2 className="font-display text-2xl md:text-3xl tracking-tight text-white mt-14 mb-5 leading-snug">
          {block.v}
        </h2>
      );
    case "quote":
      return (
        <blockquote className="my-10 border-l-2 border-amber-400 pl-6 md:pl-8">
          <p className="font-display text-xl md:text-2xl italic text-white/85 leading-snug">
            {block.v}
          </p>
        </blockquote>
      );
    case "ul":
      return (
        <ul className="my-6 space-y-3">
          {block.v.map((li) => (
            <li key={li} className="flex gap-3 text-white/70 leading-relaxed">
              <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
              <span>{li}</span>
            </li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol className="my-6 space-y-4 counter-reset">
          {block.v.map((li, i) => (
            <li key={li} className="flex gap-4 text-white/70 leading-relaxed">
              <span className="font-mono-accent text-xs text-amber-400 pt-1 shrink-0 w-5">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span>{li}</span>
            </li>
          ))}
        </ol>
      );
    default:
      return <p className="my-5 text-white/70 leading-[1.8] text-base md:text-lg">{block.v}</p>;
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) return <Navigate to="/blog" replace />;

  const url = `${SITE_URL}/blog/${post.slug}`;
  const related = sortedPosts.filter((p) => p.slug !== post.slug).slice(0, 3);

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.metaDescription,
      image: post.image,
      datePublished: post.date,
      dateModified: post.date,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      author: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
      publisher: {
        "@type": "Organization",
        name: SITE_NAME,
        url: SITE_URL,
        logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon-192.png` },
      },
      keywords: post.keywords,
      articleSection: post.category,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
        { "@type": "ListItem", position: 3, name: post.title, item: url },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#070B14] text-white font-[Manrope,sans-serif] overflow-x-hidden">
      <Seo
        title={post.metaTitle}
        description={post.metaDescription}
        path={`/blog/${post.slug}`}
        image={post.image}
        type="article"
        keywords={post.keywords}
        schema={schema}
      />
      <Nav />

      <main data-testid="blog-post-page" className="pt-28 md:pt-36">
        <article className="max-w-3xl mx-auto px-6 md:px-10">
          <nav aria-label="Breadcrumb" className="mb-8">
            <Link
              to="/blog"
              data-testid="blog-back-link"
              className="inline-flex items-center gap-2 text-xs font-mono-accent uppercase tracking-[0.2em] text-white/50 hover:text-amber-400 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              All articles
            </Link>
          </nav>

          <div className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-amber-400">
            {post.category}
          </div>

          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-semibold tracking-tightest leading-[1.06] text-white">
            {post.title}
          </h1>

          <div className="mt-7 flex flex-wrap items-center gap-6 text-xs text-white/45 font-mono-accent uppercase tracking-[0.15em] pb-8 border-b border-white/8">
            <span className="inline-flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5" /> {fmt(post.date)}
            </span>
            <span className="inline-flex items-center gap-2">
              <Clock className="w-3.5 h-3.5" /> {post.readTime}
            </span>
            <span>By {SITE_NAME}</span>
          </div>

          <motion.figure
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 overflow-hidden rounded-3xl border border-white/10"
          >
            <img
              src={post.image}
              alt={post.imageAlt}
              loading="eager"
              className="w-full h-64 md:h-[420px] object-cover"
            />
          </motion.figure>

          <div className="mt-12">
            {post.content.map((b, i) => (
              <Block key={i} block={b} />
            ))}
          </div>

          {/* Post CTA */}
          <div className="mt-20 relative overflow-hidden rounded-[2rem] border border-amber-400/25 bg-gradient-to-br from-amber-400/12 via-white/[0.02] to-transparent p-8 md:p-12">
            <div className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-amber-400">
              Next step
            </div>
            <h2 className="mt-4 font-display text-2xl md:text-3xl tracking-tight text-white leading-snug">
              Get a free growth audit for your practice.
            </h2>
            <p className="mt-4 text-white/65 leading-relaxed max-w-xl">
              Thirty minutes with a dental growth strategist. You leave with a live
              competitor breakdown, a keyword and demand audit, and a realistic ninety-day
              new-patient forecast. No pitch, no pressure.
            </p>
            <Link
              to="/#booking"
              data-testid="blog-post-cta"
              className="mt-8 group inline-flex items-center gap-2 rounded-full bg-amber-400 text-[#070B14] px-7 h-12 font-medium hover:bg-amber-300 transition-colors duration-200"
            >
              Book a Strategy Call
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" />
            </Link>
            <p className="mt-6 text-xs text-white/40">
              Questions first? Email{" "}
              <a
                href={`mailto:${EMAILS.support}`}
                className="text-amber-400/80 hover:text-amber-400 underline decoration-dotted"
              >
                {EMAILS.support}
              </a>
            </p>
          </div>
        </article>

        {/* Related */}
        <section className="max-w-7xl mx-auto px-6 md:px-10 mt-24 mb-24">
          <h2 className="font-display text-2xl md:text-3xl tracking-tight text-white mb-8">
            Keep reading
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`related-post-${p.slug}`}
                className="group rounded-3xl border border-white/10 bg-white/[0.025] overflow-hidden hover:border-amber-400/40 transition-colors duration-300"
              >
                <img
                  src={p.image}
                  alt={p.imageAlt}
                  loading="lazy"
                  className="w-full h-40 object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="p-6">
                  <div className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-amber-400">
                    {p.category}
                  </div>
                  <h3 className="mt-3 font-display text-lg text-white leading-snug group-hover:text-amber-400 transition-colors">
                    {p.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
